import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, Loader2, Plus, Trash, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { 
  usePayRun, 
  useAddPayRunItem, 
  useUpdatePayRunItem 
} from '@/hooks/usePayRuns';
import { useEmployees } from '@/hooks/useEmployees';
import { PayRunAllowance, PayRunDeduction } from '@/types';
import { formatCurrency } from '@/lib/utils';

const ALLOWANCE_TYPES = [
  { value: 'meals', label: 'Meals' },
  { value: 'travel', label: 'Travel' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'tools', label: 'Tools' },
  { value: 'other', label: 'Other' },
];

const DEDUCTION_TYPES = [
  { value: 'salary_sacrifice', label: 'Salary Sacrifice' },
  { value: 'union_fees', label: 'Union Fees' },
  { value: 'private_health', label: 'Private Health' },
  { value: 'child_support', label: 'Child Support' },
  { value: 'garnishee', label: 'Garnishee Order' },
  { value: 'other', label: 'Other' },
];

export function PayRunItemForm() {
  const { id, itemId } = useParams<{ id: string; itemId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = itemId && itemId !== 'new';

  const { data: payRun, isLoading: isLoadingPayRun } = usePayRun(id!);
  const { data: employeesData, isLoading: isLoadingEmployees } = useEmployees({ status: 'active' });
  const addPayRunItem = useAddPayRunItem();
  const updatePayRunItem = useUpdatePayRunItem();

  const employees = employeesData?.data || [];
  const existingItem = payRun?.items?.find(item => item.id === itemId);

  const [formData, setFormData] = useState({
    employeeId: '',
    ordinaryHours: 38,
    ordinaryRate: 30,
    overtimeHours: 0,
    overtimeRate: 45,
    allowances: [] as PayRunAllowance[],
    deductions: [] as PayRunDeduction[],
  });

  const [calculated, setCalculated] = useState({
    ordinaryAmount: 0,
    overtimeAmount: 0,
    totalAllowances: 0,
    totalDeductions: 0,
    gross: 0,
    tax: 0,
    super: 0,
    net: 0,
  });

  useEffect(() => {
    if (existingItem) {
      setFormData({
        employeeId: existingItem.employeeId,
        ordinaryHours: existingItem.ordinaryHours,
        ordinaryRate: existingItem.ordinaryRate,
        overtimeHours: existingItem.overtimeHours,
        overtimeRate: existingItem.overtimeRate,
        allowances: existingItem.allowances || [],
        deductions: existingItem.deductions || [],
      });
    }
  }, [existingItem]);

  useEffect(() => {
    calculateTotals();
  }, [formData]);

  const calculateTotals = () => {
    const ordinaryAmount = formData.ordinaryHours * formData.ordinaryRate;
    const overtimeAmount = formData.overtimeHours * formData.overtimeRate;
    const totalAllowances = formData.allowances.reduce((sum, a) => sum + a.amount, 0);
    const totalDeductions = formData.deductions.reduce((sum, d) => sum + d.amount, 0);
    const gross = ordinaryAmount + overtimeAmount + totalAllowances;
    
    // Simple tax calculation (this should use proper Australian tax tables)
    const estimatedTax = gross * 0.20; // Rough estimate
    const superAmount = gross * 0.115; // 11.5% SG rate
    const net = gross - estimatedTax - totalDeductions;

    setCalculated({
      ordinaryAmount,
      overtimeAmount,
      totalAllowances,
      totalDeductions,
      gross,
      tax: estimatedTax,
      super: superAmount,
      net,
    });
  };

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddAllowance = () => {
    setFormData((prev) => ({
      ...prev,
      allowances: [
        ...prev.allowances,
        { id: crypto.randomUUID(), type: 'other', description: '', amount: 0 },
      ],
    }));
  };

  const handleUpdateAllowance = (index: number, field: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      allowances: prev.allowances.map((a, i) => i === index ? { ...a, [field]: value } : a),
    }));
  };

  const handleRemoveAllowance = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      allowances: prev.allowances.filter((_, i) => i !== index),
    }));
  };

  const handleAddDeduction = () => {
    setFormData((prev) => ({
      ...prev,
      deductions: [
        ...prev.deductions,
        { id: crypto.randomUUID(), type: 'other', description: '', amount: 0 },
      ],
    }));
  };

  const handleUpdateDeduction = (index: number, field: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      deductions: prev.deductions.map((d, i) => i === index ? { ...d, [field]: value } : d),
    }));
  };

  const handleRemoveDeduction = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      deductions: prev.deductions.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.employeeId) {
      toast({
        title: 'Error',
        description: 'Please select an employee',
        variant: 'destructive',
      });
      return;
    }

    const payload = {
      ...formData,
      ordinaryAmount: calculated.ordinaryAmount,
      overtimeAmount: calculated.overtimeAmount,
      totalAllowances: calculated.totalAllowances,
      totalDeductions: calculated.totalDeductions,
      gross: calculated.gross,
      tax: calculated.tax,
      superContribution: calculated.super,
      netPay: calculated.net,
    };

    try {
      if (isEditing) {
        await updatePayRunItem.mutateAsync({ payRunId: id!, itemId: itemId!, data: payload });
        toast({
          title: 'Success',
          description: 'Pay item updated successfully',
          variant: 'success',
        });
      } else {
        await addPayRunItem.mutateAsync({ payRunId: id!, data: payload });
        toast({
          title: 'Success',
          description: 'Pay item added successfully',
          variant: 'success',
        });
      }
      navigate(`/payroll/pay-runs/${id}`);
    } catch {
      toast({
        title: 'Error',
        description: isEditing ? 'Failed to update pay item' : 'Failed to add pay item',
        variant: 'destructive',
      });
    }
  };

  if (isLoadingPayRun || isLoadingEmployees) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const isSubmitting = addPayRunItem.isPending || updatePayRunItem.isPending;
  const selectedEmployee = employees.find(e => e.id === formData.employeeId);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditing ? 'Edit Pay Item' : 'Add Pay Item'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {payRun?.payRunNumber} • {payRun?.periodStartDate} to {payRun?.periodEndDate}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Employee Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Employee</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="employeeId">Select Employee *</Label>
                  <Select
                    value={formData.employeeId}
                    onValueChange={(value) => handleChange('employeeId', value)}
                    disabled={!!isEditing}
                  >
                    <SelectTrigger id="employeeId">
                      <SelectValue placeholder="Select an employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((employee) => (
                        <SelectItem key={employee.id} value={employee.id}>
                          {employee.firstName} {employee.lastName} ({employee.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedEmployee && (
                    <p className="text-sm text-muted-foreground">
                      Base rate: {formatCurrency(selectedEmployee.payRate)}/{selectedEmployee.payType === 'hourly' ? 'hr' : 'yr'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Hours and Rates */}
            <Card>
              <CardHeader>
                <CardTitle>Hours & Rates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ordinaryHours">Ordinary Hours</Label>
                    <Input
                      id="ordinaryHours"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.ordinaryHours}
                      onChange={(e) => handleChange('ordinaryHours', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ordinaryRate">Ordinary Rate ($/hr)</Label>
                    <Input
                      id="ordinaryRate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.ordinaryRate}
                      onChange={(e) => handleChange('ordinaryRate', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="overtimeHours">Overtime Hours</Label>
                    <Input
                      id="overtimeHours"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.overtimeHours}
                      onChange={(e) => handleChange('overtimeHours', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="overtimeRate">Overtime Rate ($/hr)</Label>
                    <Input
                      id="overtimeRate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.overtimeRate}
                      onChange={(e) => handleChange('overtimeRate', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Allowances */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Allowances</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={handleAddAllowance}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.allowances.map((allowance, index) => (
                  <div key={allowance.id} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs">Type</Label>
                      <Select
                        value={allowance.type}
                        onValueChange={(value) => handleUpdateAllowance(index, 'type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ALLOWANCE_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-[2]">
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={allowance.description}
                        onChange={(e) => handleUpdateAllowance(index, 'description', e.target.value)}
                        placeholder="Description"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Amount</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allowance.amount}
                        onChange={(e) => handleUpdateAllowance(index, 'amount', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveAllowance(index)}
                    >
                      <Trash className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                {formData.allowances.length === 0 && (
                  <p className="text-muted-foreground text-center py-4">No allowances added</p>
                )}
              </CardContent>
            </Card>

            {/* Deductions */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Deductions</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={handleAddDeduction}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.deductions.map((deduction, index) => (
                  <div key={deduction.id} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs">Type</Label>
                      <Select
                        value={deduction.type}
                        onValueChange={(value) => handleUpdateDeduction(index, 'type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DEDUCTION_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-[2]">
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={deduction.description}
                        onChange={(e) => handleUpdateDeduction(index, 'description', e.target.value)}
                        placeholder="Description"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Amount</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={deduction.amount}
                        onChange={(e) => handleUpdateDeduction(index, 'amount', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveDeduction(index)}
                    >
                      <Trash className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                {formData.deductions.length === 0 && (
                  <p className="text-muted-foreground text-center py-4">No deductions added</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary Card */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Pay Calculation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Ordinary Pay</span>
                    <span>{formatCurrency(calculated.ordinaryAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Overtime Pay</span>
                    <span>{formatCurrency(calculated.overtimeAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Allowances</span>
                    <span className="text-success">+{formatCurrency(calculated.totalAllowances)}</span>
                  </div>
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between font-medium">
                    <span>Gross</span>
                    <span>{formatCurrency(calculated.gross)}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-destructive">
                    <span>Tax (estimated)</span>
                    <span>-{formatCurrency(calculated.tax)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-destructive">
                    <span>Deductions</span>
                    <span>-{formatCurrency(calculated.totalDeductions)}</span>
                  </div>
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Net Pay</span>
                    <span className="text-success">{formatCurrency(calculated.net)}</span>
                  </div>
                </div>
                <div className="pt-2 text-sm text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Super Contribution</span>
                    <span>{formatCurrency(calculated.super)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="mr-2 h-4 w-4" />
            {isEditing ? 'Update Pay Item' : 'Add Pay Item'}
          </Button>
        </div>
      </form>
    </div>
  );
}
